<template>
  <div v-if="isFileFetching" class="tw:absolute tw:top-0 tw:left-0 tw:w-full tw:h-full tw:z-10 tw:flexcenter">
    <div class="tw:bg-white tw:rounded-base tw:px-4 tw:py-3.5">
      <div class="tw:flexrow-3 tw:flexcenter">
        <main-loader :size="19" :width="2" />
        <span class="tw:font-medium tw:text-sm tw:text-gray-700">{{ $i18n.t('default.loadingData') }}</span>
      </div>
    </div>
  </div>
  <label
    v-else
    ref="dropzone"
    id="drop-zone"
    @drop="drop"
    @dragover="dragover"
    @dragenter="$event => $event.stopPropagation()"
    @dragleave="isOver = false"
    :style="{
      backgroundImage: customBorder,
    }"
    class="tw:absolute tw:top-0 tw:left-0 tw:w-full tw:h-full tw:z-10 tw:rounded-base tw:bg-[rgba(203,224,250,0.3)]"
    :class="{ 'tw:block': isOver, 'tw:hidden': !isOver }"
  >
    <div class="tw:h-full tw:w-full tw:flexcenter tw:font-medium tw:text-lg"></div>
    <input type="file" id="file-input" :accept="availableFormats.join(',')" class="tw:hidden" />
  </label>
</template>

<script>
import { get, call } from 'vuex-pathify';

import MainLoader from '@/components/MainLoader';

export default {
  name: 'MapDropSpace',
  components: {
    MainLoader,
  },
  data: () => ({
    isFileFetching: false,
    isOver: false,
  }),
  computed: {
    cursorPositionEpsg: get('map/cursorPositionEpsg'),
    currentCursorSrid: get('map/coordinatesEpsgs'),
    availableFormats() {
      return ['.dxf', '.geojson'];
    },
    customBorder() {
      const svgData = `data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='${this.$_colors.primary.replace('#', '%23')}' stroke-width='6' stroke-dasharray='16%2c 16' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e`;
      return `url("${svgData}")`;
    },
  },
  methods: {
    readUploadedFile: call('layers/readUploadedFile'),

    getFileExtension(file) {
      return `.${file.name.split('.').at(-1)}`;
    },

    /** TODO: this should be read from global formats dictionary */
    getFormatReadableName(file) {
      switch (this.getFileExtension(file)) {
        case '.geojson':
          return 'GeoJSON';
        case '.dxf':
          return 'DXF';
      }
    },
    /** Formats parsers */
    async processGeojson(file) {
      try {
        const fileTextData = await file.text();
        let geojson = JSON.parse(fileTextData);

        /**
         * Respect saved coordinate system inside geojson.
         * If it is not present, take current map coordinate system
         * */
        if (!geojson.crs)
          geojson.crs = {
            type: 'name',
            properties: {
              name: this.cursorPositionEpsg,
            },
          };

        this.$root.$emit('addTemporaryLayer', {
          geometry: geojson,
          name: file.name,
        });
      } catch (error) {
        throw new Error(error);
      }
    },
    async processDxf(file) {
      try {
        const formData = new FormData();
        formData.append('files', file);
        const data = await this.readUploadedFile({
          formData,
          srid: this.currentCursorSrid[this.cursorPositionEpsg]?.auth_srid,
        });
        const fileGeometryData = Object.values(data)[0];
        const orderGeometryTypes = ['point', 'multipoint', 'linestring', 'multilinestring', 'polygon', 'multipolygon'];
        const unorderedGeometries = Object.keys(fileGeometryData).filter(type => !orderGeometryTypes.includes(type));
        [...orderGeometryTypes, ...unorderedGeometries].reverse().forEach(type => {
          if (!fileGeometryData[type]) return;
          const layerName = `${file.name} - ${this.$i18n.t(`default.geometries.${type}`)}`;
          this.$root.$emit('addTemporaryLayer', { geometry: fileGeometryData[type], name: layerName });
        });
      } catch (error) {
        throw new Error(error);
      }
    },
    async processFormat(file) {
      const extension = this.getFileExtension(file);
      switch (extension) {
        case '.geojson':
          return await this.processGeojson(file);
        case '.dxf':
          return await this.processDxf(file);
        default:
          throw new Error(`Format '${extension}' not recognized`);
      }
    },

    /** Dropzone events */
    async drop(e) {
      /** Do not proceed if format is wrong - it is read from name extension because type is not accessible from different OS and browsers */
      const file = e.dataTransfer.files[0];
      if (!this.availableFormats.includes(this.getFileExtension(file))) return;

      this.isFileFetching = true;
      try {
        /** Handle file upload */
        await this.processFormat(file);
        this.isOver = false;
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('snackbar.dragDropFile', { format: this.getFormatReadableName(file) }),
        });
      } catch (error) {
        console.error(error);
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('snackbar.dragDropDataError'),
        });
      } finally {
        this.isFileFetching = false;
      }
    },
    dragover(e) {
      const fileItems = [...e.dataTransfer.items].filter(item => item.kind === 'file');
      if (!fileItems.length) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = fileItems?.length ? 'copy' : 'none';
    },

    /** Parent events */
    parentDragenter() {
      this.isOver = true;
    },
    parentDrop(e) {
      if ([...e.dataTransfer.items].some(item => item.kind === 'file')) e.preventDefault();
      this.isOver = false;
    },
    parentDragover(e) {
      const fileItems = [...e.dataTransfer.items].filter(item => item.kind === 'file');
      if (!fileItems.length) return;
      e.preventDefault();
      if (!this.$refs.dropzone.contains(e.target)) e.dataTransfer.dropEffect = 'none';
    },

    /** Events handler */
    handleParentEvents(remove = false) {
      const parentElement = this.$parent.$el;
      [
        [parentElement, 'dragenter', this.parentDragenter],
        [parentElement, 'dragover', this.parentDragover],
        [window, 'drop', this.parentDrop],
      ].forEach(([element, ev, method]) => element[remove ? 'removeEventListener' : 'addEventListener'](ev, method));
    },
  },
  beforeDestroy() {
    this.handleParentEvents(true);
  },
  mounted() {
    this.handleParentEvents();
  },
};
</script>

<style scoped>
.custom-dash {
  stroke: aqua;
}
</style>
